import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router";
import { Button, Form, Space, Upload } from "antd";
import { isNil, size } from "lodash";
import { LEAVE_CONFIRMATION } from "constants/common";
import { AiccLessonEntry, AiccLessonEntryPayload, LessonEntryAssetConfig } from "types/model/aiccLearningAssets";
import AgenticAssetLink from "../AgenticAssetLink";
import LearningAssetPage from "../LearningAssetPage";
import LessonEntryFilters from "../LessonEntryFilters";
import { skipLearningAssetConfirm } from "../confirmations";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";
import { useLessonEntryFilters } from "../hooks/useLessonEntryFilters";
import { useLessonEntryPagination } from "../hooks/useLessonEntryPagination";
import { useLessonEntryEdit } from "../hooks/useLessonEntryEdit";
import { useLessonEntryExport } from "../hooks/useLessonEntryExport";
import { useAssetImport } from "../hooks/useAssetImport";
import UploadPreviewModal from "../UploadPreviewModal";
import getColumns, { getPreviewColumns } from "./columnConfig";
import LessonEntryModal from "./LessonEntryModal";

const FILE_ACCEPT = ".zip,.xlsx,.xls";

interface Props {
  config: LessonEntryAssetConfig;
}

function LessonEntryPage({ config }: Props) {
  const { api, textLabel } = config;
  const state = useLearningAssetVersions(api.fetchVersions);
  const {
    languageId,
    items,
    filters: lessonFilters,
    hasTransliteration,
    groupStatus,
    isEditable,
    isDraft,
    canManage,
    canDelete,
    isLoading,
    isSaving,
    runWrite,
  } = state;

  const {
    entries,
    levelOptions,
    lessonOptions,
    getLessonOptions,
    selectedLevels,
    selectedLessons,
    setSelectedLevels,
    setSelectedLessons,
  } = useLessonEntryFilters(items, lessonFilters);

  const canDownload = !isNil(groupStatus) && !isDraft;

  const { editForm, editingId, startEdit, cancelEdit, saveEdit, handleValuesChange } = useLessonEntryEdit(
    api,
    runWrite
  );
  const { uploadFile, preview, isValidating, closeUpload, selectFile, confirmUpload } = useAssetImport({
    api,
    languageId,
    isDraft,
    runWrite,
  });
  const { isDownloading, download } = useLessonEntryExport({
    api,
    exportName: config.exportName,
    languageId,
    groupStatus,
  });

  const [isAddVisible, setIsAddVisible] = useState(false);
  const isEditing = !isNil(editingId);
  const isUploading = isValidating || !isNil(preview);
  const isRowActionDisabled = isEditing || isUploading;
  const isActionDisabled = isNil(languageId) || isRowActionDisabled;
  const previewRows = preview?.rows ?? [];

  useEffect(() => {
    cancelEdit();
  }, [cancelEdit, groupStatus, languageId]);

  const handleDelete = useCallback(
    (entry: AiccLessonEntry) => {
      runWrite((id) => api.remove(id, entry), `Deleted ${entry.text} successfully.`);
    },
    [api, runWrite]
  );

  const closeAdd = useCallback(() => setIsAddVisible(false), []);

  const handleAdd = useCallback(
    (payload: AiccLessonEntryPayload) => {
      runWrite((id) => api.create(id, payload), `Added ${payload.text} successfully.`, closeAdd, {
        confirm: skipLearningAssetConfirm,
      });
    },
    [api, closeAdd, runWrite]
  );

  const columns = useMemo(
    () =>
      getColumns({
        textLabel,
        hasTransliteration,
        canManage,
        canDelete,
        isActionDisabled: isRowActionDisabled,
        isSaving,
        editingId,
        levelOptions,
        getLessonOptions,
        onEdit: startEdit,
        onDelete: handleDelete,
        onSave: saveEdit,
        onCancelEdit: cancelEdit,
      }),
    [
      cancelEdit,
      canDelete,
      canManage,
      editingId,
      getLessonOptions,
      handleDelete,
      hasTransliteration,
      isRowActionDisabled,
      isSaving,
      levelOptions,
      saveEdit,
      startEdit,
      textLabel,
    ]
  );

  const isFilterDisabled = isLoading || isRowActionDisabled;

  const pagination = useLessonEntryPagination(entries, groupStatus, isEditing);

  const actions = (
    <Space size={12}>
      {canManage && (
        <Button type="primary" shape="round" disabled={isActionDisabled} onClick={() => setIsAddVisible(true)}>
          {config.addText}
        </Button>
      )}
      {isEditable && (
        <Upload accept={FILE_ACCEPT} showUploadList={false} beforeUpload={selectFile} disabled={isActionDisabled}>
          <Button type="primary" shape="round" loading={isValidating} disabled={isActionDisabled}>
            Upload
          </Button>
        </Upload>
      )}
      {canDownload && (
        <Button type="primary" shape="round" loading={isDownloading} disabled={isActionDisabled} onClick={download}>
          Download Current Data
        </Button>
      )}
    </Space>
  );

  return (
    <Form form={editForm} component={false} onValuesChange={handleValuesChange}>
      <Prompt when={isEditing || isSaving} message={LEAVE_CONFIRMATION} />
      <LearningAssetPage<AiccLessonEntry>
        title={config.title}
        headerExtra={config.hasAgenticAssetLink && <AgenticAssetLink />}
        fieldGuideIntro={config.fieldGuideIntro}
        columns={columns}
        state={{ ...state, items: entries }}
        pagination={pagination}
        isEditing={isEditing}
        isLocked={isUploading}
        filters={
          <LessonEntryFilters
            levelOptions={levelOptions}
            lessonOptions={lessonOptions}
            selectedLevels={selectedLevels}
            selectedLessons={selectedLessons}
            setSelectedLevels={setSelectedLevels}
            setSelectedLessons={setSelectedLessons}
            disabled={isFilterDisabled}
          />
        }
        toolbarActions={isNil(groupStatus) ? undefined : actions}
      >
        <LessonEntryModal
          visible={isAddVisible}
          title={`Add ${config.title}`}
          textLabel={textLabel}
          levelOptions={levelOptions}
          getLessonOptions={getLessonOptions}
          hasTransliteration={hasTransliteration}
          isSaving={isSaving}
          onCancel={closeAdd}
          onSubmit={handleAdd}
        />
        <UploadPreviewModal<AiccLessonEntryPayload>
          visible={!isNil(preview)}
          fileName={uploadFile?.name}
          preview={preview}
          rows={previewRows}
          columns={getPreviewColumns(textLabel, preview?.hasTransliteration)}
          rowKey={({ level, lesson, text }) => `${level}-${lesson}-${text}`}
          summary={`${size(
            previewRows
          )} rows are ready to upload. Uploading replaces all current content for this language.`}
          isSaving={isSaving}
          onCancel={closeUpload}
          onConfirm={confirmUpload}
        />
      </LearningAssetPage>
    </Form>
  );
}

export default LessonEntryPage;
